import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import {
  FileTextOutlined,
  StarOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  ShopOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  ToolOutlined,
  CloseOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { Drawer, Button, Typography } from 'antd';

const { Sider } = Layout;
const { Text } = Typography;

function Sidebar(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState('');
  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    const path = location.pathname;
    setSelectedKey(path);

    // Collapsed icon rail uses popup submenus. Do not keep them open from
    // the current route — that looks like a hover flyout after every navigate.
    if (props.collapsed && !props.isMobile) {
      setOpenKeys([]);
      return;
    }

    const pathSegments = path.split('/').filter(Boolean);
    if (pathSegments.length > 1) {
      const parentKey = pathSegments[0];
      setOpenKeys((prev) => {
        if (!prev.includes(parentKey)) {
          return [...prev, parentKey];
        }
        return prev;
      });
    }
  }, [location.pathname, props.collapsed, props.isMobile]);

  const handleMenuClick = (e) => {
    navigate(e.key);
    if (props.collapsed && !props.isMobile) {
      setOpenKeys([]);
    }
    if (props.isMobile) {
      props.onCloseMobileMenu();
    }
  };

  const handleOpenChange = (keys) => {
    const rootSubmenuKeys = menuItems.map((item) => item.key);
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeys(keys);
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const menuItems = [
    {
      key: 'order',
      icon: <FileTextOutlined />,
      label: 'คำสั่งซื้อ',
      children: [
        {
          key: '/order/payslip',
          label: 'ตรวจสอบสลิปโอนเงิน',
        },
        {
          key: '/order/refund',
          label: 'คืนเงินผู้ซื้อ',
        },
        {
          key: '/order/income',
          label: 'จ่ายเงินผู้ขาย',
        },
        {
          key: '/order/tracking',
          label: 'ติดตามคำสั่งซื้อ',
        },
      ],
    },
    {
      key: 'extra',
      icon: <StarOutlined />,
      label: 'ส่วนเสริม',
      children: [
        {
          key: '/extra/coupon',
          label: 'คูปองส่วนลด',
        },
      ],
    },
    {
      key: 'product',
      icon: <AppstoreOutlined />,
      label: 'สินค้า',
      children: [
        {
          key: '/product/category',
          label: 'หมวดหมู่สินค้า',
        },
        {
          key: '/product/category/license',
          label: 'สิทธิ์การเข้าถึงหมวดหมู่สินค้า',
        },
        {
          key: '/product/item',
          label: 'ตารางรายการสินค้า',
        },
        {
          key: '/product/itempreview',
          label: 'ตัวอย่างสินค้า',
        },
      ],
    },
    {
      key: 'reference',
      icon: <DatabaseOutlined />,
      label: 'ข้อมูลอ้างอิง',
      children: [
        {
          key: '/reference/parcel',
          label: 'พัสดุ',
        },
        {
          key: '/reference/factor',
          label: 'ปัจจัย',
        },
      ],
    },
    {
      key: 'accountshop',
      icon: <ShopOutlined />,
      label: 'บัญชีร้านค้า',
      children: [
        {
          key: '/accountshop/accountapproved',
          label: 'ร้านค้าที่อนุมัติแล้ว',
        },
        {
          key: '/accountshop/accountpending',
          label: 'ร้านค้าที่รอการอนุมัติ',
        },
        {
          key: '/accountshop/accountsuspended',
          label: 'ร้านค้าที่ถูกระงับ',
        },
      ],
    },
    {
      key: 'job',
      icon: <IdcardOutlined />,
      label: 'ระบบหางาน',
      children: [
        {
          key: '/job/posts',
          label: 'ประกาศรับสมัครงาน',
        },
        {
          key: '/job/employers',
          label: 'ผู้ว่าจ้าง / สถานพยาบาล',
        },
        {
          key: '/job/seekers',
          label: 'ผู้สมัครงาน (Seekers)',
        },
        {
          key: '/job/categories',
          label: 'หมวดหมู่วิชาชีพ',
        },
        {
          key: '/job/overview',
          label: 'รายงานและสถิติภาพรวม',
        },
      ],
    },
    // Office Account - Admin only
    ...(props.permitdata.role === 'admin'
      ? [
          {
            key: '/accountoffice',
            icon: <UserOutlined />,
            label: 'บัญชีเจ้าหน้าที่',
          },
        ]
      : []),
    {
      key: 'helpdesk',
      icon: <CustomerServiceOutlined />,
      label: 'ศูนย์ช่วยเหลือ',
      children: [
        {
          key: '/helpdesk/chat',
          label: 'แชท',
        },
      ],
    },
    // API Setting - Admin only
    ...(props.permitdata.role === 'admin'
      ? [
          {
            key: 'apisetting',
            icon: <ToolOutlined />,
            label: 'ตั้งค่า API',
            children: [
              {
                key: '/apisetting/sms',
                label: 'SMS',
              },
              {
                key: '/apisetting/email',
                label: 'อีเมล',
              },
              {
                key: '/apisetting/delivery',
                label: 'การจัดส่ง',
              },
            ],
          },
        ]
      : []),
  ];

  const sidebarContent = (
    <div style={{ padding: '16px 0' }}>
      <div
        style={{
          color: '#94a3b8',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          padding: '0 24px',
          marginBottom: '8px',
          display: props.collapsed && !props.isMobile ? 'none' : 'block',
        }}
      >
        เลือกเมนู
      </div>
      <Menu
        mode='inline'
        theme='light'
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        items={menuItems}
        style={{ borderRight: 0 }}
      />
    </div>
  );

  if (props.isMobile) {
    return (
      <Drawer
        placement='left'
        onClose={props.onCloseMobileMenu}
        open={props.mobileMenuOpen}
        closable={false}
        width={280}
        styles={{
          body: { padding: 0, background: '#ffffff' },
          mask: { backdropFilter: 'blur(4px)' },
        }}
      >
        <div
          style={{
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e2e8f0',
            height: '64px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img
              src='/images/Meya-logo1.png'
              alt='Meya Logo'
              style={{ height: '32px', width: 'auto' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
              <Text strong style={{ color: '#1e293b', fontSize: '16px', margin: 0 }}>
                MEYA
              </Text>
              <Text style={{ color: '#64748b', fontSize: '10px', margin: 0 }}>
                Management Platform
              </Text>
            </div>
          </div>
          <Button
            type='text'
            icon={<CloseOutlined style={{ color: '#64748b', fontSize: '18px' }} />}
            onClick={props.onCloseMobileMenu}
            style={{ padding: 0, width: '32px', height: '32px' }}
          />
        </div>
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={props.collapsed}
      onCollapse={props.onCollapse}
      width={250}
      style={{
        overflow: 'auto',
        height: 'calc(100vh - 64px)',
        position: 'fixed',
        left: 0,
        top: 64,
        bottom: 0,
        zIndex: 99,
        background: '#ffffff',
        borderRight: '1px solid #e2e8f0',
      }}
      collapsedWidth={80}
      theme='light'
    >
      {sidebarContent}
    </Sider>
  );
}

Sidebar.propTypes = {
  permitdata: PropTypes.shape({
    role: PropTypes.string,
  }),
  collapsed: PropTypes.bool,
  onCollapse: PropTypes.func,
  isMobile: PropTypes.bool,
  mobileMenuOpen: PropTypes.bool,
  onCloseMobileMenu: PropTypes.func,
};

export default Sidebar;
